import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, SaveOutlined, SendOutlined, ShopOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import ResponsiveTable from '../../components/responsive/ResponsiveTable';
import StickyActionBar from '../../components/responsive/StickyActionBar';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import { createMR, updateMR, submitMR, getMR, getLookups, getCostCenterConfig, costCenterLabel, catalogGet, catalogItemPrices, catalogBrowse } from '../../api';
import type { CatalogSearchResult, CostCenterRef, ItemPrices } from '../../api';
import { useAuth } from '../../context/AuthContext';
import CostCenterSelect from '../../components/CostCenterSelect';
import CatalogBrowserModal from '../../components/CatalogBrowserModal';
import type { PickedCatalogItem, ExistingRequestLine } from '../../components/CatalogBrowserModal';

const { Title, Text } = Typography;
const { TextArea } = Input;

/**
 * Requester's MR screen (2026-09-06 redesign, approved on the mockup):
 *  - pick items in the catalog browser, type a quantity; unit defaults from the catalog and can be
 *    switched (marker shown); items with no catalog unit ask for one;
 *  - free-text "Not in catalog" lines stay allowed;
 *  - estimated unit price only when the department has `requesterEntersPrice`;
 *  - no vendor / category / currency here — procurement sets those.
 * Procurement, approvers and admins keep the full grid (MRCreate).
 */

interface Line {
  key: string;
  catalogItemId: number | null;
  partNumber: string;
  description: string;
  descriptionAr?: string | null;
  catalogUnitId: number | null; // null = catalog has no unit (or free text)
  unitId?: number;
  quantity: number;
  estPrice?: number | null;
  vendorId?: number | null;
  vendorName?: string;            // free text: a vendor not in the ERP yet (becomes Pending)
  vendorSource?: 'requester' | 'department' | null;
  vendorLabel?: string;           // name of a vendor not in the active list (e.g. a Pending one)
  vendorOptions?: ItemPrices['vendors']; // vendors that quote this item (names only for requesters)
  deptPreferences?: ItemPrices['deptPreferences'];
  currencyId?: number | null;
  costCenterId?: number | null;
  costCenter?: CostCenterRef | null;
  notes: string;
}

interface Lookups {
  departments: Array<{ id: number; name: string; code: string; requesterEntersPrice?: boolean }>;
  units: Array<{ id: number; name: string; category?: string | null }>;
  currencies: Array<{ id: number; code: string; name: string }>;
  vendors: Array<{ id: number; name: string }>;
}

let keyCounter = 0;
const nextKey = () => `rl_${++keyCounter}`;

const mono: React.CSSProperties = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 600 };

const MRCreateRequester: React.FC = () => {
  const navigate = useNavigate();
  const { mrId: editMrId } = useParams<{ mrId?: string }>();
  const isEdit = Boolean(editMrId);
  const { user, myDepartmentIds, isProcurement, isApprover, isAdmin, canAccess } = useAuth();
  // Procurement, approvers (CEO) and admins get the same catalog-first screen plus the
  // procurement fields on every line (vendor, unit price, currency) and prices in the browser.
  const fullEdit = isProcurement || isApprover || isAdmin;
  const canPickAnyDepartment = canAccess('purchasing.mrs', 'full');
  // 2026-09-23: a requester who covers several departments has to choose one deliberately.
  // The home department used to be prefilled with nothing on screen to say so, which is how
  // MR-DES-000062 and MR-DES-000065 were raised on Design when both were Embroidery work.
  const mustPickDepartment = myDepartmentIds.length > 1;
  const [form] = Form.useForm();
  const departmentId = Form.useWatch('departmentId', form);
  const awaitingDepartment = mustPickDepartment && !departmentId;

  const [lookups, setLookups] = useState<Lookups>({ departments: [], units: [], currencies: [], vendors: [] });
  const [lines, setLines] = useState<Line[]>([]);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isCompactLayout = useCompactLayout(); // phones + small tablets (2026-10-19)

  const [defaultCostCenterId, setDefaultCostCenterId] = useState<number | null>(null);
  const [defaultCostCenter, setDefaultCostCenter] = useState<CostCenterRef | null>(null);
  const [ccRequired, setCcRequired] = useState(false);
  useEffect(() => { getCostCenterConfig().then((c) => setCcRequired(!!c.requiredNow)).catch(() => {}); }, []);

  const showEstPrice = useMemo(
    () => fullEdit || !!lookups.departments.find((d) => d.id === departmentId)?.requesterEntersPrice,
    [lookups.departments, departmentId, fullEdit]
  );
  const unitName = (id?: number | null) => lookups.units.find((u) => u.id === id)?.name ?? '';
  const defaultCurrencyId = () => (lookups.currencies.find((c) => c.code === 'EGP') || lookups.currencies[0])?.id;

  useEffect(() => {
    getLookups()
      .then((data) => {
        setLookups({ departments: data.departments || [], units: data.units || [], currencies: data.currencies || [], vendors: data.vendors || [] });
        if (!isEdit && !mustPickDepartment && user?.departmentId) form.setFieldValue('departmentId', user.departmentId);
      })
      .catch(() => message.error('Failed to load form data'));
  }, [form, user, isEdit, mustPickDepartment]);

  // Edit an existing draft: rebuild lines, fetching the catalog unit for linked items.
  useEffect(() => {
    if (!isEdit || !editMrId) return;
    (async () => {
      try {
        const mr = await getMR(editMrId);
        if (mr.status !== 'Draft') { message.error('Only Draft MRs can be edited'); navigate(`/mrs/${editMrId}`); return; }
        form.setFieldsValue({
          departmentId: mr.departmentId,
          neededBy: mr.neededBy ? dayjs(mr.neededBy) : undefined,
          urgency: mr.urgency,
          notes: mr.notes || '',
        });
        setDefaultCostCenterId(mr.defaultCostCenterId || null);
        setDefaultCostCenter(mr.defaultCostCenter || null);
        const built: Line[] = [];
        for (const l of mr.lines || []) {
          let catalogUnitId: number | null = null;
          let descriptionAr: string | null = null;
          if (l.catalogItemId) {
            try { const c = await catalogGet(l.catalogItemId); catalogUnitId = c.unit?.id ?? null; descriptionAr = c.descriptionAr ?? null; } catch { /* keep as free text */ }
          }
          built.push({
            key: nextKey(),
            catalogItemId: l.catalogItemId || null,
            partNumber: l.partNumber || '',
            description: l.itemDescription || '',
            descriptionAr,
            catalogUnitId,
            unitId: l.unitId || undefined,
            quantity: Number(l.qty) || 1,
            estPrice: Number(l.unitPrice) || null,
            vendorId: l.vendorId || l.vendor?.id || null,
            vendorSource: l.vendorSource || null,
            vendorLabel: l.vendor?.name || undefined,
            currencyId: l.currencyId || l.currency?.id || null,
            costCenterId: l.costCenterId || null,
            costCenter: l.costCenter || null,
            notes: l.lineNotes || '',
          });
        }
        setLines(built);
        for (const b of built) if (b.catalogItemId) loadVendorChoices(b.key, b.catalogItemId);
      } catch {
        message.error('Failed to load MR for editing');
        navigate('/mrs');
      }
    })();
  }, [isEdit, editMrId]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyDefaultCostCenter = (next: number | null, cc: CostCenterRef | null) => {
    const prev = defaultCostCenterId;
    setDefaultCostCenterId(next);
    setDefaultCostCenter(cc);
    setLines((ls) => ls.map((l) => (!l.costCenterId || l.costCenterId === prev ? { ...l, costCenterId: next, costCenter: cc } : l)));
  };

  const update = (key: string, patch: Partial<Line>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  // Vendors that quote the item + standing department preferences (one call per catalog line).
  const loadVendorChoices = (key: string, catalogItemId: number) => {
    catalogItemPrices(catalogItemId).then((p) => setLines((ls) => ls.map((l) => {
      if (l.key !== key) return l;
      const pref = p.deptPreferences.find((d) => d.departmentId === departmentId);
      const next: Line = { ...l, vendorOptions: p.vendors, deptPreferences: p.deptPreferences };
      if (!l.vendorId && !l.vendorName && pref && !fullEdit) { next.vendorId = pref.vendorId; next.vendorSource = 'department'; }
      return next;
    }))).catch(() => {});
  };
  const remove = (key: string) => setLines((ls) => ls.filter((l) => l.key !== key));

  // 2026-09-16: every pick is a new line (the same item may repeat; Create MR checks the cost centers).
  const addPicked = (picked: PickedCatalogItem[]) => {
    const keyed = picked.map((p) => ({ p, key: nextKey() }));
    setLines((ls) => {
      const next = [...ls];
      for (const { p, key } of keyed) {
        next.push({
          key,
          catalogItemId: p.item.id,
          partNumber: p.item.partNumber,
          description: p.item.description,
          descriptionAr: p.item.descriptionAr ?? null,
          catalogUnitId: p.item.unit?.id ?? null,
          unitId: p.item.unit?.id ?? undefined,
          quantity: p.qty,
          estPrice: fullEdit && p.item.defaultUnitPrice != null ? Number(p.item.defaultUnitPrice) : null,
          vendorId: fullEdit ? p.item.preferredVendor?.id ?? null : null,
          currencyId: fullEdit ? p.item.currency?.id ?? null : null,
          costCenterId: defaultCostCenterId,
          costCenter: defaultCostCenter,
          notes: '',
        });
      }
      return next;
    });
    for (const { p, key } of keyed) setTimeout(() => loadVendorChoices(key, p.item.id), 0);
    setBrowserOpen(false);
  };

  /** Lines that share a catalog item AND a cost center (none counts as a cost center). */
  const duplicateGroups = (ls: Line[]) => {
    const groups = new Map<string, number[]>();
    ls.forEach((l, i) => {
      if (!l.catalogItemId) return;
      const k = `${l.catalogItemId}|${l.costCenterId ?? 'none'}`;
      groups.set(k, [...(groups.get(k) || []), i]);
    });
    return Array.from(groups.values()).filter((idx) => idx.length > 1);
  };

  /** "also on line n" for a catalog line that appears elsewhere on the request. */
  const alsoOnLines = (r: Line) => lines
    .map((l, i) => ({ l, no: i + 1 }))
    .filter(({ l }) => l.key !== r.key && l.catalogItemId && l.catalogItemId === r.catalogItemId)
    .map(({ no }) => no);

  // Free-text nudge: when a "Not in catalog" description looks like a catalog item, offer it.
  const [suggest, setSuggest] = useState<Record<string, CatalogSearchResult | null>>({});
  const [dismissed, setDismissed] = useState<Record<string, string>>({});
  const suggestTimer = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const askSuggestion = (key: string, text: string) => {
    if (suggestTimer.current[key]) clearTimeout(suggestTimer.current[key]);
    const q = text.trim();
    if (q.length < 3) { setSuggest((s) => ({ ...s, [key]: null })); return; }
    suggestTimer.current[key] = setTimeout(async () => {
      try {
        const r = await catalogBrowse({ q, page: 1, pageSize: 1 });
        setSuggest((s) => ({ ...s, [key]: r.items[0] ?? null }));
      } catch { /* nudge only */ }
    }, 450);
  };
  const applySuggestion = (r: Line, item: CatalogSearchResult) => {
    update(r.key, {
      catalogItemId: item.id, partNumber: item.partNumber, description: item.description, descriptionAr: item.descriptionAr ?? null,
      catalogUnitId: item.unit?.id ?? null, unitId: r.unitId ?? item.unit?.id ?? undefined,
    });
    setSuggest((s) => ({ ...s, [r.key]: null }));
    setTimeout(() => loadVendorChoices(r.key, item.id), 0);
  };

  const addFreeText = () => {
    setLines((ls) => [...ls, {
      key: nextKey(), catalogItemId: null, partNumber: '', description: '', descriptionAr: null,
      catalogUnitId: null, unitId: undefined, quantity: 1, estPrice: null,
      costCenterId: defaultCostCenterId, costCenter: defaultCostCenter, notes: '',
    }]);
  };

  const allocationSummary = () => {
    const groups = new Map<string, number>();
    for (const l of lines) {
      const label = l.costCenterId ? (l.costCenter ? costCenterLabel(l.costCenter) : `#${l.costCenterId}`) : 'No cost center';
      groups.set(label, (groups.get(label) || 0) + 1);
    }
    return Array.from(groups.entries());
  };

  const handleSave = async (shouldSubmit: boolean) => {
    try {
      const values = await form.validateFields();
      if (lines.length === 0) { message.error('Add at least one item'); return; }
      if (lines.some((l) => !l.description.trim())) { message.error('Every "Not in catalog" line needs a description'); return; }
      if (lines.some((l) => !(l.quantity > 0))) { message.error('Quantity must be greater than zero on every line'); return; }

      // 2026-09-16: same item on the same cost center twice → merge, or go back and fix.
      const dupGroups = duplicateGroups(lines);
      if (dupGroups.length > 0) {
        const merge = await new Promise<boolean | null>((resolve) => {
          const dialog = Modal.confirm({
            title: dupGroups.length === 1 ? 'Two lines are the same item on the same cost center' : 'Some lines are the same item on the same cost center',
            width: 560,
            okText: 'Merge into one line',
            cancelText: 'Go back and fix',
            content: (
              <div>
                {dupGroups.map((idx) => {
                  const first = lines[idx[0]];
                  const cc = first.costCenterId ? (first.costCenter ? costCenterLabel(first.costCenter) : `#${first.costCenterId}`) : 'no cost center';
                  const total = idx.reduce((s, i) => s + Number(lines[i].quantity || 0), 0);
                  return (
                    <div key={idx.join('-')} style={{ marginBottom: 10 }}>
                      <div><Tag style={mono}>{first.partNumber}</Tag>{first.description} · <strong>{cc}</strong></div>
                      <div style={{ fontSize: 12.5, color: '#4b5563' }}>
                        lines {idx.map((i) => i + 1).join(', ')} ({idx.map((i) => `${lines[i].quantity} ${unitName(lines[i].unitId)}`).join(' + ')}) → merged: line {idx[0] + 1} = {total} {unitName(first.unitId)}
                      </div>
                    </div>
                  );
                })}
                <Text type="secondary" style={{ fontSize: 12.5 }}>Merge adds the quantities into the first line. Go back to keep both lines and give one a different cost center.</Text>
              </div>
            ),
            onOk: () => { dialog.destroy(); resolve(true); },
            onCancel: () => { dialog.destroy(); resolve(false); },
          });
        });
        if (!merge) return;
        const drop = new Set<number>();
        const merged = lines.map((l) => ({ ...l }));
        for (const idx of dupGroups) {
          merged[idx[0]].quantity = idx.reduce((s, i) => s + Number(lines[i].quantity || 0), 0);
          idx.slice(1).forEach((i) => drop.add(i));
        }
        const kept = merged.filter((_, i) => !drop.has(i));
        setLines(kept);
        message.info(`${drop.size} line${drop.size === 1 ? '' : 's'} merged. Check the request, then save again.`);
        return;
      }
      const noUnit = lines.filter((l) => !l.unitId).length;
      if (noUnit > 0) { message.error(`Choose a unit on ${noUnit} line${noUnit === 1 ? '' : 's'}`); return; }

      if (shouldSubmit) {
        const missing = lines.filter((l) => !l.costCenterId).length;
        if (ccRequired && missing > 0) {
          message.error(`Cost center is required on every line before submitting (${missing} line${missing === 1 ? '' : 's'} missing)`);
          return;
        }
        const summary = allocationSummary();
        const confirmed = await new Promise<boolean>((resolve) => {
          const dialog = Modal.confirm({
            title: 'Confirm cost allocation',
            okText: 'Submit',
            cancelText: 'Back',
            content: (
              <div>
                <p style={{ marginBottom: 8 }}>This request will be charged to:</p>
                <ul style={{ paddingLeft: 18, margin: 0 }}>
                  {summary.map(([label, n]) => (
                    <li key={label} style={{ color: label === 'No cost center' ? '#cf1322' : undefined }}>
                      {n} line{n === 1 ? '' : 's'} → <strong>{label}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            ),
            onOk: () => { dialog.destroy(); resolve(true); },
            onCancel: () => { dialog.destroy(); resolve(false); },
          });
        });
        if (!confirmed) return;
        setSubmitting(true);
      } else {
        setSaving(true);
      }

      const currencyId = defaultCurrencyId();
      const mrData = {
        departmentId: values.departmentId,
        neededBy: values.neededBy.format('YYYY-MM-DD'),
        urgency: values.urgency,
        notes: values.notes || '',
        defaultCostCenterId: defaultCostCenterId || null,
        lines: lines.map((l) => ({
          catalogItemId: l.catalogItemId || undefined,
          partNumber: l.partNumber || undefined,
          itemDescription: l.description.trim(),
          qty: l.quantity,
          unitId: l.unitId,
          // Requester's estimate (kept as orig_unit_price once procurement refines the line).
          unitPrice: showEstPrice && l.estPrice ? l.estPrice : 0,
          vendorId: l.vendorId || undefined,
          vendorName: !l.vendorId && l.vendorName ? l.vendorName.trim() : undefined,
          vendorSource: l.vendorId || l.vendorName ? (l.vendorSource === 'department' ? 'department' : 'requester') : undefined,
          currencyId: (fullEdit && l.currencyId) || currencyId,
          lineNotes: l.notes || undefined,
          costCenterId: l.costCenterId || null,
        })),
      };

      let mrRef: string | number;
      if (isEdit && editMrId) {
        const updated = await updateMR(editMrId, mrData);
        mrRef = updated.id || updated.mrId;
        if (shouldSubmit && mrRef) { await submitMR(mrRef as number); message.success('Request updated and submitted'); }
        else message.success('Request updated');
      } else {
        const created = await createMR(mrData);
        mrRef = created.id || created.mrId;
        if (shouldSubmit && mrRef) { await submitMR(mrRef as number); message.success('Request submitted'); }
        else message.success('Request saved as draft');
      }
      navigate(`/mrs/${mrRef}`, { replace: true });
    } catch (err: any) {
      if (err?.errorFields) return; // form validation already shows the message
      message.error(err.response?.data?.error || err.message || 'Failed to save request');
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  };

  const columns = [
    { title: '#', width: 44, render: (_: any, __: Line, i: number) => i + 1 },
    {
      title: 'Item', key: 'item', width: 240,
      render: (_: any, r: Line) => {
        if (!r.catalogItemId) return <Tag color="orange">Not in catalog</Tag>;
        const also = alsoOnLines(r);
        return (
          <div>
            <Tag style={mono}>{r.partNumber}</Tag>
            {also.length > 0 && <div style={{ fontSize: 11, color: '#8a94a6' }}>also on line{also.length > 1 ? 's' : ''} {also.join(', ')}</div>}
          </div>
        );
      },
    },
    {
      title: 'Description', key: 'desc', width: 360,
      render: (_: any, r: Line) => r.catalogItemId ? (
        <div style={{ lineHeight: 1.35 }}>
          <div>{r.description}</div>
          {r.descriptionAr && <div dir="rtl" style={{ color: '#666', textAlign: 'left' }}>{r.descriptionAr}</div>}
        </div>
      ) : (
        <div>
          <Input
            value={r.description}
            status={r.description.trim() ? undefined : 'error'}
            placeholder="Describe the item (name, size, colour, anything that helps procurement)"
            onChange={(e) => { update(r.key, { description: e.target.value }); askSuggestion(r.key, e.target.value); }}
          />
          {(() => {
            const s = suggest[r.key];
            if (!s || dismissed[r.key] === r.description.trim()) return <Text type="secondary" style={{ fontSize: 12 }}>Procurement will assign a code before ordering.</Text>;
            return (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: '#e6efff', border: '1px solid #1e5fb8', borderRadius: 6, padding: '4px 8px', marginTop: 4, fontSize: 12.5 }}>
                <span>Looks like <Tag style={mono}>{s.partNumber}</Tag>{s.description}{s.descriptionAr ? <span dir="rtl" style={{ color: '#4b5563', marginLeft: 6 }}>{s.descriptionAr}</span> : null}</span>
                <Space size={4}>
                  <Button size="small" type="primary" onClick={() => applySuggestion(r, s)}>Use this item</Button>
                  <Button size="small" onClick={() => setDismissed((d) => ({ ...d, [r.key]: r.description.trim() }))}>Keep as free text</Button>
                </Space>
              </div>
            );
          })()}
        </div>
      ),
    },
    {
      title: 'Quantity', key: 'qty', width: 250, align: 'right' as const,
      render: (_: any, r: Line) => {
        const changed = r.catalogUnitId && r.unitId && r.unitId !== r.catalogUnitId;
        const missing = !r.unitId;
        return (
          <div>
            <Space>
              <InputNumber min={0.01} step={1} style={{ width: 90 }} value={r.quantity} onChange={(v) => update(r.key, { quantity: Number(v) || 0 })} />
              <Select
                size="middle" style={{ width: 120 }} showSearch optionFilterProp="label"
                status={missing ? 'warning' : undefined}
                placeholder="Choose unit"
                value={r.unitId}
                options={lookups.units.map((u) => ({ value: u.id, label: u.name }))}
                onChange={(v) => update(r.key, { unitId: v })}
              />
            </Space>
            {changed && <div style={{ fontSize: 11, color: '#b26a00' }}>changed from {unitName(r.catalogUnitId)}</div>}
            {missing && r.catalogItemId && <div style={{ fontSize: 11, color: '#b26a00' }}>no unit in catalog yet</div>}
          </div>
        );
      },
    },
    ...(showEstPrice ? [{
      title: fullEdit ? 'Unit price' : <Tooltip title="Your estimate. Procurement sets the final price."><span>Est. unit price</span></Tooltip>, key: 'est', width: 140, align: 'right' as const,
      render: (_: any, r: Line) => (
        <InputNumber min={0} step={1} precision={2} style={{ width: 120 }} placeholder="0.00" value={r.estPrice ?? undefined} onChange={(v) => update(r.key, { estPrice: v === null ? null : Number(v) })} />
      ),
    }] : []),
    ...(!fullEdit ? [{
      title: <span>Vendor <span style={{ fontWeight: 400, color: '#888' }}>(optional)</span></span>, key: 'reqVendor', width: 200,
      render: (_: any, r: Line) => {
        const opts = r.vendorOptions || [];
        const value = r.vendorName ? '__other' : (r.vendorId ?? '__any');
        return (
          <div>
            <Select size="middle" style={{ width: 200 }} showSearch optionFilterProp="label" value={value}
              options={[{ value: '__any', label: 'Any vendor' }, ...opts.map((v) => ({ value: v.vendorId, label: v.vendorName })), ...lookups.vendors.filter((v) => !opts.some((o) => o.vendorId === v.id)).map((v) => ({ value: v.id, label: v.name })), ...(r.vendorId && r.vendorLabel && !opts.some((o) => o.vendorId === r.vendorId) && !lookups.vendors.some((v) => v.id === r.vendorId) ? [{ value: r.vendorId, label: `${r.vendorLabel} (pending approval)` }] : []), { value: '__other', label: 'Other… (type a name)' }]}
              onChange={(v) => v === '__any' ? update(r.key, { vendorId: null, vendorName: '', vendorSource: null }) : v === '__other' ? update(r.key, { vendorId: null, vendorName: r.vendorName || ' ', vendorSource: 'requester' }) : update(r.key, { vendorId: Number(v), vendorName: '', vendorSource: 'requester' })} />
            {r.vendorName !== undefined && r.vendorName !== '' && <Input size="small" style={{ marginTop: 4 }} placeholder="Vendor name" value={r.vendorName.trim()} onChange={(e) => update(r.key, { vendorName: e.target.value || ' ' })} />}
            {r.vendorSource === 'department' && r.vendorId && <div style={{ fontSize: 11, color: '#1677ff' }}>{lookups.departments.find((d) => d.id === departmentId)?.name || 'Department'}'s usual vendor</div>}
            {opts.length > 0 && <div style={{ fontSize: 11, color: '#999' }}>quotes: {opts.map((v) => v.vendorName).join(' · ')}</div>}
          </div>
        );
      },
    }] : []),
    ...(fullEdit ? [{
      title: 'Currency', key: 'cur', width: 110,
      render: (_: any, r: Line) => (
        <Select size="middle" style={{ width: 96 }} value={r.currencyId ?? defaultCurrencyId()} options={lookups.currencies.map((c) => ({ value: c.id, label: c.code }))} onChange={(v) => update(r.key, { currencyId: v })} />
      ),
    }, {
      title: 'Vendor', key: 'vendor', width: 200,
      render: (_: any, r: Line) => (
        <Select size="middle" allowClear showSearch optionFilterProp="label" style={{ width: 184 }} placeholder="Vendor" value={r.vendorId ?? undefined}
          options={lookups.vendors.map((v) => ({ value: v.id, label: v.name }))} onChange={(v) => update(r.key, { vendorId: v ?? null })} />
      ),
    }] : []),
    {
      title: 'Cost center · Notes', key: 'cc', width: 240,
      render: (_: any, r: Line) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <CostCenterSelect
            size="small"
            value={r.costCenterId ?? null}
            current={r.costCenter || null}
            status={ccRequired && !r.costCenterId ? 'error' : undefined}
            placeholder={ccRequired ? 'Cost center *' : 'Cost center'}
            onChange={(id, cc) => update(r.key, { costCenterId: id, costCenter: cc })}
          />
          <Input size="small" placeholder="Notes for procurement" value={r.notes} onChange={(e) => update(r.key, { notes: e.target.value })} />
        </div>
      ),
    },
    {
      title: '', key: 'x', width: 44,
      render: (_: any, r: Line) => (
        <Popconfirm title="Remove this line?" onConfirm={() => remove(r.key)} okText="Remove" cancelText="Keep">
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const existingLines = useMemo<ExistingRequestLine[]>(() => lines.map((l, i) => ({
    lineNo: i + 1, catalogItemId: l.catalogItemId, partNumber: l.partNumber, description: l.description, qty: l.quantity, unit: unitName(l.unitId),
  })), [lines, lookups.units]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <Title level={4}>{isEdit ? 'Edit Material Request' : 'Create Material Request'}</Title>

      <Card style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="departmentId"
                label="Department"
                rules={[{ required: true, message: 'Select department' }]}
                extra={awaitingDepartment ? `You cover ${myDepartmentIds.length} departments — choose the one this MR is charged to.` : undefined}
              >
                <Select
                  placeholder="Select department" showSearch optionFilterProp="label"
                  status={awaitingDepartment ? 'warning' : undefined}
                  options={lookups.departments.filter((d) => canPickAnyDepartment || myDepartmentIds.includes(d.id)).map((d) => ({ label: d.name, value: d.id }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="neededBy" label="Needed by" rules={[{ required: true, message: 'Select date' }]}>
                <DatePicker style={{ width: '100%' }} disabledDate={(c) => c && c < dayjs().startOf('day')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="urgency" label="Urgency" rules={[{ required: true, message: 'Select urgency' }]} initialValue="Normal">
                <Select options={['Low', 'Normal', 'High', 'Critical'].map((u) => ({ label: u, value: u }))} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                label={<span>Allocate to {ccRequired ? <Tag color="red" style={{ marginLeft: 6 }}>required</Tag> : <Tag style={{ marginLeft: 6 }}>optional</Tag>}</span>}
                required={ccRequired}
                tooltip="Applied to every line; you can change a single line below."
              >
                <CostCenterSelect
                  value={defaultCostCenterId}
                  onChange={applyDefaultCostCenter}
                  placeholder="Project / collection / client › sub"
                  status={ccRequired && !defaultCostCenterId && lines.some((l) => !l.costCenterId) ? 'error' : undefined}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="notes" label="Notes"><TextArea rows={1} placeholder="Optional notes" /></Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card
        title="Items"
        extra={
          <Space>
            <Button icon={<EditOutlined />} onClick={addFreeText}>Not in catalog</Button>
            <Button type="primary" icon={<ShopOutlined />} onClick={() => setBrowserOpen(true)}>Browse catalog</Button>
          </Space>
        }
      >
        {lines.length === 0 ? (
          <Alert
            type="info" showIcon
            message="No items yet"
            description='Press "Browse catalog" to search by code, English or Arabic description and add items with a quantity. Use "Not in catalog" for anything that has no code yet.'
            action={<Button type="primary" onClick={() => setBrowserOpen(true)}>Browse catalog</Button>}
          />
        ) : (
          <ResponsiveTable card={{ header: ['item', 'desc'], footer: ['x'] }} columns={columns} dataSource={lines} rowKey="key" pagination={false} size="small" scroll={{ x: fullEdit ? 1650 : 1280 }} />
        )}

        <div style={{ marginTop: 12, fontSize: 12.5, color: '#666', borderLeft: '3px solid #1677ff', paddingLeft: 10 }}>
          Units default from the catalog and can be switched per line. Cost center defaults to the header and can be changed per line.
          {fullEdit ? ' Prices default from the catalog; vendor defaults to the preferred vendor. Both can be changed here or later via Refine.' : showEstPrice ? ' Your estimated price is shown to procurement as an estimate; they set the final price.' : ' Prices and vendors are set by procurement after approval.'}
        </div>

        {isCompactLayout ? (
          // Phones + small tablets (2026-10-19, M3-a): the main actions sit in a bar fixed to the bottom.
          <StickyActionBar>
            <span style={{ flex: '1 1 auto', fontSize: 13, color: '#5f6672' }}>{lines.length} item{lines.length === 1 ? '' : 's'}</span>
            <Button icon={<SaveOutlined />} loading={saving} onClick={() => handleSave(false)}>Save draft</Button>
            <Button type="primary" icon={<SendOutlined />} loading={submitting} onClick={() => handleSave(true)}>Submit</Button>
          </StickyActionBar>
        ) : (
          <Row justify="end" style={{ marginTop: 16 }}>
            <Space>
              <Button onClick={() => navigate('/mrs')}>Cancel</Button>
              <Button icon={<SaveOutlined />} loading={saving} onClick={() => handleSave(false)}>Save as draft</Button>
              <Button type="primary" icon={<SendOutlined />} loading={submitting} onClick={() => handleSave(true)}>Save &amp; submit</Button>
            </Space>
          </Row>
        )}
      </Card>

      <CatalogBrowserModal
        open={browserOpen}
        onClose={() => setBrowserOpen(false)}
        onDone={addPicked}
        existingLines={existingLines}
        showPrice={fullEdit}
      />
    </div>
  );
};

export default MRCreateRequester;
